import { Injectable } from '@angular/core';
import { SubmitCashbackSuccess } from '@features/cashback/store/actions/cashback.actions';
import { OrderApiService } from '@features/order/services/order-api/order-api.service';
import { TransferPointsSuccess } from '@features/points-transfer/store/actions/points-transfer.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Order } from '@rd/types';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SelectOrder } from './../../../../order/src/lib/order/store/actions/order.actions';
import { PollOrder, PollOrderFail, PollOrderSuccess } from './polling.actions';

type ActionTriggerPolling =
  | ReturnType<typeof TransferPointsSuccess>
  | ReturnType<typeof SubmitCashbackSuccess>;

@Injectable()
export class PollingEffects {
  constructor(
    private actions$: Actions,
    private orderApiService: OrderApiService,
  ) {}

  triggerPollOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransferPointsSuccess, SubmitCashbackSuccess),
      switchMap((action: ActionTriggerPolling) => {
        const orderId = this.getOrderId(action);
        if (!orderId) {
          return EMPTY;
        }
        return of(PollOrder({ orderId }), new SelectOrder(orderId));
      }),
    ),
  );

  pollOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PollOrder),
      switchMap(({ orderId }) =>
        this.orderApiService.pollOrder(orderId).pipe(
          map((order) => PollOrderSuccess({ order } as { order: Order })),
          catchError((err) =>
            of(
              PollOrderFail({
                error: err,
                orderId,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  private getOrderId(action: ActionTriggerPolling): string | undefined {
    switch (action.type) {
      case TransferPointsSuccess.type:
      case SubmitCashbackSuccess.type:
        return action.orderId;

      default:
        const exhaustiveCheck: never = action;
        throw new Error(`Unhandled action type: ${exhaustiveCheck}`);
    }
  }
}
